import { useAuth0 } from '@auth0/auth0-react';

// Shared Auth0 service hook
export const useAuthService = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    // RCS-69. The SDK does NOT throw when a login fails - handleRedirectCallback
    // rejects and the reason lands here, on the context. Nothing read it, so a
    // failed sign-in looked identical to "not signed in yet" and the caller
    // redirected to Auth0 again. On dev2 that was 133 round trips in 25 minutes,
    // each one succeeding at the token endpoint and being discarded, with
    // nothing on screen and nothing in the console.
    error
  } = useAuth0();

  // Login with session preservation
  const login = async (preserveSession = false) => {
    const appState = {
      returnTo: window.location.pathname + window.location.search
    };

    if (preserveSession) {
      const anonymousSessionId = localStorage.getItem('sessionId');
      if (anonymousSessionId) {
        localStorage.setItem('anonymousSessionId', anonymousSessionId);
        appState.preserveSession = anonymousSessionId;
      }
    }

    await loginWithRedirect({
      appState,
      authorizationParams: {
        redirect_uri: window.location.origin + '/admin'
      }
    });
  };

  // Logout
  const logoutUser = async () => {
    await logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  // Get access token for API calls
  const getToken = async () => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      // Call without params - audience is already set in Auth0Provider
      const token = await getAccessTokenSilently();
      return token;
    } catch (error) {
      console.error('[Auth] Error getting access token:', error.message);
      return null;
    }
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout: logoutUser,
    getToken,
    // Whatever the SDK could not do, and why. A caller that ignores this can
    // only ever show a blank screen.
    error
  };
};

// Auth0 configuration factory
export const createAuth0Config = (clientId, domain) => ({
  domain: domain || process.env.REACT_APP_AUTH0_DOMAIN,
  clientId,
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: 'openid profile email'
  }
});
